import React, { useState, useEffect } from 'react';
import { ProDocTemplateEditorProps } from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import './pro-doc-template-editor.scss';

interface ProDocTemplateEditorComponentProps extends ProDocTemplateEditorProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

const ProDocTemplateEditor: React.FC<ProDocTemplateEditorComponentProps> = ({
  templateId,
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [managerType, setManagerType] = useState('');
  const [sampleDoc, setSampleDoc] = useState('');
  const [suitableFor, setSuitableFor] = useState('');
  const [guidance, setGuidance] = useState('');

  useEffect(() => {
    fetchTemplate();
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get(`/pro-doc-templates/${templateId}`);

      if (response.data.success && response.data.template) {
        const template = response.data.template;
        setManagerType(template.managerType);
        setSampleDoc(template.sampleDoc);
        setSuitableFor(template.suitableFor);
        setGuidance(template.guidance);
      } else {
        setError(response.data.error || 'Failed to load template');
      }
    } catch (err: any) {
      console.error('Error fetching template:', err);
      setError(
        err.response?.data?.error ||
          'Failed to load template. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Validate required fields
      if (!managerType.trim()) {
        setError('Manager Type is required');
        setSaving(false);
        return;
      }
      if (!sampleDoc.trim()) {
        setError('Sample Doc is required');
        setSaving(false);
        return;
      }
      if (!suitableFor.trim()) {
        setError('Suitable For is required');
        setSaving(false);
        return;
      }
      if (!guidance.trim()) {
        setError('Guidance is required');
        setSaving(false);
        return;
      }

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.put(`/pro-doc-templates/${templateId}`, {
        templateId,
        managerType,
        sampleDoc,
        suitableFor,
        guidance,
      });

      if (response.data.success) {
        setSuccessMessage('Template saved successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.data.error || 'Failed to save template');
      }
    } catch (err: any) {
      console.error('Error saving template:', err);
      setError(
        err.response?.data?.error ||
          'Failed to save template. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='pro-doc-template-editor'>
        <div className='loading-spinner'>
          <div className='spinner'></div>
          <p>Loading template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='pro-doc-template-editor'>
      <div className='editor-header'>
        <h3>Edit ProDoc Template</h3>
      </div>

      {error && (
        <div className='message error-message'>
          <span className='icon'>⚠️</span>
          {error}
        </div>
      )}

      {successMessage && (
        <div className='message success-message'>
          <span className='icon'>✅</span>
          {successMessage}
        </div>
      )}

      <div className='editor-form'>
        <div className='form-group'>
          <label htmlFor='managerType'>
            Manager Type <span className='required'>*</span>
          </label>
          <input
            id='managerType'
            type='text'
            value={managerType}
            onChange={(e) => setManagerType(e.target.value)}
            placeholder='e.g., Business Manager, Sales Manager'
            disabled={saving}
          />
        </div>

        <div className='form-group'>
          <label htmlFor='sampleDoc'>
            Sample Doc <span className='required'>*</span>
          </label>
          <textarea
            id='sampleDoc'
            value={sampleDoc}
            onChange={(e) => setSampleDoc(e.target.value)}
            placeholder='Enter a sample document showing the structure and content...'
            rows={12}
            disabled={saving}
          />
        </div>

        <div className='form-group'>
          <label htmlFor='suitableFor'>
            Suitable For <span className='required'>*</span>
          </label>
          <textarea
            id='suitableFor'
            value={suitableFor}
            onChange={(e) => setSuitableFor(e.target.value)}
            placeholder='Describe what situations or use cases this template is suitable for...'
            rows={6}
            disabled={saving}
          />
        </div>

        <div className='form-group'>
          <label htmlFor='guidance'>
            Guidance <span className='required'>*</span>
          </label>
          <textarea
            id='guidance'
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
            placeholder='Provide guidance on how to use this template and when to recommend it...'
            rows={6}
            disabled={saving}
          />
        </div>

        <div className='form-actions'>
          <button
            className='save-button'
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className='button-spinner'></span>
                Saving...
              </>
            ) : (
              'Save Template'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProDocTemplateEditor;
